import * as React from 'react'

import { DutyBranch } from '@/features/duties/branches'

/**
 * The branch a view of a duty is showing. Page state, not server state.
 *
 * Scoped to branch-aware prompt cards through `useAuthedDutyScriptQuery`.
 * Every other card is branchless and shows Live. Any view that renders branch-aware
 * prompt content must sit under a provider, whether or not it shows a switcher —
 * the Calling view and the Playground are separate views by that measure and each
 * provides its own.
 *
 * This context answers which branch a view shows. It does not answer whether
 * content is editable — that also depends on the archived state and on whether
 * the branch is one the user chose. So each card settles editability for itself
 * and passes the answer to its parts as a prop. A shared part is told; it never
 * asks.
 */
const DutyBranchContext = React.createContext<DutyBranch | undefined>(undefined)

export function DutyBranchProvider({
  branch,
  children,
}: {
  branch: DutyBranch
  children: React.ReactNode
}) {
  return (
    <DutyBranchContext.Provider value={branch}>
      {children}
    </DutyBranchContext.Provider>
  )
}

/**
 * Throws rather than guessing: a default here is a silent wrong answer, and the
 * caller is always a view that knows which copy it wants.
 *
 * The card chrome in `DutyCard` is shared with the evals case-detail page, which
 * renders no duty content and so has no branch; it takes what it needs as props
 * and never reaches this.
 */
export function useDutyBranch(): DutyBranch {
  const branch = React.useContext(DutyBranchContext)
  if (!branch) {
    throw new Error('useDutyBranch must be used within a DutyBranchProvider')
  }
  return branch
}

/**
 * Whether the branch on screen is Edit. Only Edit's content can be written, so
 * this gates edit affordances on branch-aware prompt cards. The API does not
 * enforce it — it stops the UI offering an edit on a branch the user did not
 * mean to change.
 */
export function useIsDutyEditBranch(): boolean {
  return useDutyBranch() === DutyBranch.Edit
}
